package com.bikompanion.data.parking

import dev.zacsweers.metro.BindingContainer
import dev.zacsweers.metro.ContributesTo
import dev.zacsweers.metro.Provides
import dev.zacsweers.metro.SingleIn
import com.bikompanion.data.cache.BikeDataCache
import com.bikompanion.domain.ParkingRepository
import com.bikompanion.domain.di.AppScope

@ContributesTo(AppScope::class)
@BindingContainer
object ParkingProviders {
    /**
     * Racks come from the dataset shipped with the app, everywhere in the world.
     *
     * The layering reads outside-in: the disk cache holds what was served, the bundled dataset
     * answers from the file, and Overpass is only reached for when that file cannot be opened.
     * Overpass used to be the whole of it, and that is precisely why the layer did not work — see
     * [BundledParkingRepository].
     *
     * The cache still earns its place around a local file: it is what keeps a fallback answer,
     * and it means the decode of a cell happens once rather than on every camera move.
     *
     * Constructed here rather than `@Inject`ed because both the transport and the dataset come
     * from `expect fun` factories, which can't carry an annotation. Mirrors how the caching bike-
     * network repository is wired in `NetworkProviders`.
     *
     * Scoped, unlike the other repositories: this one has state worth sharing. The opened 5.5 MB
     * dataset, which instances are refusing us ([EndpointHealth]), and which areas are in flight
     * are all only useful if every caller sees the same copy — handed out fresh per injection,
     * each one would read the file again.
     */
    @SingleIn(AppScope::class)
    @Provides
    fun provideParkingRepository(cache: BikeDataCache): ParkingRepository =
        ParkingRepositoryImpl(
            delegate = BundledParkingRepository(
                fallback = OverpassParkingRepository(createOverpassTransport()),
            ),
            cache = cache,
        )
}
